/**
 * Whitepaper Download - Gestion du formulaire et du téléchargement PDF
 * XHR : évite window.fetch (surchargé par des apps) et l’iframe (CSP frame-ancestors sur /contact).
 */
(function () {
  if (window.__risuWhitepaperDownloadInit) return;
  window.__risuWhitepaperDownloadInit = true;

class WhitepaperForm extends HTMLFormElement {
  constructor() {
    super();
    this.pdfUrl = null;
    this.init();
  }

  init() {
    // Récupérer l'URL du PDF : d'abord sur le formulaire, sinon depuis le modal parent
    const rawPdf =
      this.dataset.pdfUrl ||
      (this.closest('whitepaper-download') && this.closest('whitepaper-download').dataset.pdfUrl) ||
      '';
    this.pdfUrl = this.normalizeUrl(rawPdf) || null;

    // Mode d'envoi : par email (formulaire contact Shopify)
    this.sendByEmail = this.dataset.sendEmail === 'true';
    this.shopifyContactFormSelector = (this.dataset.shopifyContactForm || '').trim();
    this.contactUrl = this.dataset.contactUrl || '/contact';
    this.contactNameField = this.dataset.contactNameField || 'Name';
    this.contactCommentField = this.dataset.contactCommentField || 'Comment';

    this.addEventListener('submit', this.handleSubmit.bind(this));
  }

  getShopifyContactForm() {
    if (!this.shopifyContactFormSelector) return null;
    try {
      return document.querySelector(this.shopifyContactFormSelector);
    } catch {
      return null;
    }
  }

  isContactPostSuccessFromHtml(html, iframeLocation) {
    const loc = (iframeLocation || '').toLowerCase();
    if (loc.includes('contact_posted=true')) return true;
    if (html.includes('alert--success') && html.includes('form-message')) return true;
    if (html.includes('form-status-list') && html.includes('alert--success')) return true;
    return false;
  }

  isContactPostErrorPage(html) {
    return html.includes('form-message--error') || html.includes('form-error alert');
  }

  isContactPostHardFailure(html) {
    if (this.isContactPostErrorPage(html)) return true;
    if (/400\s+bad request/i.test(html)) return true;
    return false;
  }

  resolveFormPostUrl(formEl) {
    let action = formEl.getAttribute('action') || this.contactUrl;
    if (action.startsWith('//')) {
      action = `${window.location.protocol}${action}`;
    } else if (action.startsWith('/')) {
      action = `${window.location.origin}${action}`;
    }
    return action.split('#')[0];
  }

  buildUrlEncodedBody(formEl) {
    const params = new URLSearchParams();
    const fd = new FormData(formEl);
    for (const pair of fd.entries()) {
      params.append(pair[0], pair[1]);
    }
    return params.toString();
  }

  evaluateContactHttpResponse(status, responseUrl, html, resolve, reject) {
    const urlLower = (responseUrl || '').toLowerCase();
    if (this.isContactPostSuccessFromHtml(html, urlLower)) {
      resolve();
      return true;
    }
    if (this.isContactPostHardFailure(html)) {
      reject(new Error('contact_error'));
      return true;
    }
    const ok = status >= 200 && status < 400;
    if (ok && !this.isContactPostErrorPage(html)) {
      resolve();
      return true;
    }
    if (ok && html.length > 500 && !this.isContactPostErrorPage(html)) {
      resolve();
      return true;
    }
    return false;
  }

  /**
   * fetch dans un Web Worker : self.fetch n’est en général pas surchargé par shop_events_listener (contrairement à window.fetch / XHR).
   */
  submitContactViaWorkerFetch(postUrl, encodedBody) {
    return new Promise((resolve, reject) => {
      const workerSrc =
        'self.onmessage=function(e){' +
        'var u=e.data.u;var b=e.data.b;' +
        'fetch(u,{method:"POST",headers:{"Content-Type":"application/x-www-form-urlencoded; charset=UTF-8",Accept:"text/html,application/xhtml+xml"},body:b,credentials:"include",redirect:"follow",mode:"cors"})' +
        '.then(function(r){return r.text().then(function(t){self.postMessage({ok:1,status:r.status,url:r.url||"",text:t});});})' +
        '.catch(function(err){self.postMessage({ok:0,err:String(err&&err.message||err)});});' +
        '};';

      const blob = new Blob([workerSrc], { type: 'text/javascript' });
      let objectUrl;
      let worker;
      try {
        objectUrl = URL.createObjectURL(blob);
        worker = new Worker(objectUrl);
      } catch (err) {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        reject(err);
        return;
      }

      const cleanup = () => {
        try {
          URL.revokeObjectURL(objectUrl);
        } catch (_) {}
        try {
          worker.terminate();
        } catch (_) {}
      };

      worker.onmessage = (ev) => {
        cleanup();
        const m = ev.data;
        if (!m.ok) {
          reject(new Error(m.err || 'worker_fetch_failed'));
          return;
        }
        const html = m.text || '';
        const responseUrl = (m.url || '').toLowerCase();
        if (
          !this.evaluateContactHttpResponse(m.status, responseUrl, html, resolve, reject)
        ) {
          console.warn('[whitepaper] Worker fetch contact', m.status, m.url);
          reject(new Error('fetch_failed'));
        }
      };

      worker.onerror = (err) => {
        cleanup();
        reject(err);
      };

      worker.postMessage({ u: postUrl, b: encodedBody });
    });
  }

  /**
   * Repli : POST navigateur vers un onglet ouvert au clic (même geste utilisateur) — pas d’iframe, pas de XHR patché.
   */
  submitContactViaNamedWindow(formEl, winName, w) {
    return new Promise((resolve, reject) => {
      formEl.setAttribute('target', winName);
      try {
        formEl.submit();
      } catch (err) {
        formEl.removeAttribute('target');
        reject(err);
        return;
      }
      formEl.removeAttribute('target');

      const start = Date.now();
      const poll = setInterval(() => {
        if (Date.now() - start > 120000) {
          clearInterval(poll);
          reject(new Error('timeout'));
          return;
        }
        if (w.closed) {
          clearInterval(poll);
          reject(new Error('window_closed'));
          return;
        }
        let href = '';
        try {
          href = w.location.href || '';
        } catch (_) {
          return;
        }
        let html = '';
        try {
          html = w.document?.documentElement?.innerHTML || '';
        } catch (_) {}
        const h = href.toLowerCase();
        if (
          h.includes('contact_posted') ||
          (html.includes('alert--success') && html.includes('form-message')) ||
          (html.includes('form-status-list') && html.includes('alert--success'))
        ) {
          clearInterval(poll);
          resolve();
        }
      }, 400);
    });
  }

  /**
   * 1) Worker fetch natif  2) sinon formulaire vers onglet réservé au clic (pop-up).
   */
  async submitContact(formEl) {
    const postUrl = this.resolveFormPostUrl(formEl);
    const body = this.buildUrlEncodedBody(formEl);

    const winName = 'risu_wp_c_' + Date.now();
    // PDF dans l’onglet réservé (même geste clic) plutôt qu’une page vide ; si repli formulaire, la réponse contact remplace cet onglet.
    const pdfReserve = this.pdfUrl ? this.normalizeUrl(this.pdfUrl) : '';
    const reserveWin = window.open(pdfReserve || 'about:blank', winName);

    try {
      await this.submitContactViaWorkerFetch(postUrl, body);
      if (reserveWin && !reserveWin.closed) {
        try {
          reserveWin.close();
        } catch (_) {}
      }
      return;
    } catch (err) {
      console.warn('[whitepaper] Worker fetch contact:', err && err.message);
    }

    if (!reserveWin || reserveWin.closed) {
      throw new Error('popup_blocked');
    }

    await this.submitContactViaNamedWindow(formEl, winName, reserveWin);
  }

  normalizeUrl(url) {
    if (!url || !String(url).trim()) return '';
    const u = String(url).trim();
    if (u.startsWith('//')) return `https:${u}`;
    if (!/^https?:\/\//i.test(u)) return `https://${u}`;
    return u;
  }


  async handleSubmit(e) {
    e.preventDefault();
    
    const submitButton = this.querySelector('.whitepaper-form__submit');
    const buttonText = submitButton.querySelector('.btn__text');
    const spinner = submitButton.querySelector('loading-spinner');
    
    // Désactiver le bouton et afficher le spinner
    submitButton.disabled = true;
    if (buttonText) buttonText.textContent = 'Envoi en cours...';
    if (spinner) spinner.style.display = 'block';

    // Récupérer les données du formulaire
    const firstnameField = this.querySelector('.whitepaper-field-firstname');
    const lastnameField = this.querySelector('.whitepaper-field-lastname');
    const emailField = this.querySelector('.whitepaper-field-email');
    const rgpdCheckbox = this.querySelector('.whitepaper-field-rgpd');
    
    // Validation
    if (!firstnameField || !firstnameField.value.trim()) {
      this.showError('Veuillez remplir tous les champs obligatoires.');
      submitButton.disabled = false;
      if (buttonText) buttonText.textContent = 'Télécharger le livre blanc';
      if (spinner) spinner.style.display = 'none';
      return;
    }

    if (!lastnameField || !lastnameField.value.trim()) {
      this.showError('Veuillez remplir tous les champs obligatoires.');
      submitButton.disabled = false;
      if (buttonText) buttonText.textContent = 'Télécharger le livre blanc';
      if (spinner) spinner.style.display = 'none';
      return;
    }

    if (!emailField || !emailField.value || !emailField.value.includes('@')) {
      this.showError('Veuillez entrer une adresse email valide.');
      submitButton.disabled = false;
      if (buttonText) buttonText.textContent = 'Télécharger le livre blanc';
      if (spinner) spinner.style.display = 'none';
      return;
    }

    if (!rgpdCheckbox || !rgpdCheckbox.checked) {
      this.showError('Veuillez accepter les conditions RGPD.');
      submitButton.disabled = false;
      if (buttonText) buttonText.textContent = 'Télécharger le livre blanc';
      if (spinner) spinner.style.display = 'none';
      return;
    }

    const now = new Date();
    const firstName = firstnameField.value.trim();
    const lastName = lastnameField.value.trim();
    const email = emailField.value.trim();
    const rgpdConsent = rgpdCheckbox.checked ? 'Accepté' : 'Non accepté';

    try {
      if (this.sendByEmail) {
        const bodyText = [
          '--- Formulaire Livre blanc ---',
          '',
          'Date : ' + now.toLocaleDateString('fr-FR'),
          'Heure : ' + now.toLocaleTimeString('fr-FR'),
          'Prénom : ' + firstName,
          'Nom : ' + lastName,
          'Email : ' + email,
          'Consentement RGPD : ' + rgpdConsent
        ].join('\n');

        const shopifyForm = this.getShopifyContactForm();

        if (shopifyForm) {
          const nameInput = shopifyForm.querySelector('.whitepaper-shopify-field--name');
          const emailInput = shopifyForm.querySelector('.whitepaper-shopify-field--email');
          const bodyInput = shopifyForm.querySelector('.whitepaper-shopify-field--body');
          if (nameInput) nameInput.value = `${firstName} ${lastName}`.trim();
          if (emailInput) emailInput.value = email;
          if (bodyInput) bodyInput.value = bodyText;

          await this.submitContact(shopifyForm);
        } else {
          const tempForm = document.createElement('form');
          tempForm.method = 'POST';
          tempForm.action = this.contactUrl;
          tempForm.setAttribute('accept-charset', 'UTF-8');
          tempForm.style.cssText = 'display:none';

          const addField = (name, value) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            tempForm.appendChild(input);
          };

          addField('utf8', '✓');
          addField('form_type', 'contact');
          addField(`contact[${this.contactNameField}]`, `${firstName} ${lastName}`.trim());
          addField('contact[email]', email);
          addField(`contact[${this.contactCommentField}]`, bodyText);

          document.body.appendChild(tempForm);
          try {
            await this.submitContact(tempForm);
          } finally {
            if (tempForm.parentNode) tempForm.remove();
          }
        }

        await this.handleSuccess();
      } else {
        // Pas d'envoi configuré : succès direct (téléchargement uniquement)
        await this.handleSuccess();
      }
    } catch (error) {
      console.error('Erreur envoi formulaire:', error);
      const msg =
        error && error.message === 'popup_blocked'
          ? 'Autorisez les fenêtres pop-up pour ce site afin d’envoyer le formulaire, puis réessayez.'
          : error && error.message === 'timeout'
            ? 'Le serveur met trop de temps à répondre. Réessayez dans un instant.'
            : 'Une erreur est survenue. Veuillez réessayer.';
      this.showError(msg);
      submitButton.disabled = false;
      if (buttonText) buttonText.textContent = 'Télécharger le livre blanc';
      if (spinner) spinner.style.display = 'none';
    }
  }

  ensurePdfDownloadLink(resolvedUrl) {
    if (!resolvedUrl) return;
    const successMessage = this.querySelector('.whitepaper-form__success');
    if (!successMessage) return;

    let actions = successMessage.querySelector('.whitepaper-form__pdf-actions');
    if (!actions) {
      actions = document.createElement('div');
      actions.className = 'whitepaper-form__pdf-actions mt-4';
      successMessage.appendChild(actions);
    }
    actions.innerHTML = '';

    const hint = document.createElement('p');
    hint.className = 'm-0 text-sm';
    hint.textContent =
      'Les navigateurs bloquent souvent le téléchargement automatique après l’envoi : utilisez le bouton ci-dessous si besoin.';

    const link = document.createElement('a');
    link.href = resolvedUrl;
    link.className = 'btn btn--primary w-full mt-2';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = 'Télécharger le PDF';

    actions.appendChild(hint);
    actions.appendChild(link);
  }

  async handleSuccess() {
    const submitButton = this.querySelector('.whitepaper-form__submit');
    const buttonText = submitButton && submitButton.querySelector('.btn__text');
    const spinner = submitButton && submitButton.querySelector('loading-spinner');
    if (submitButton) submitButton.disabled = false;
    if (buttonText) buttonText.textContent = 'Télécharger le livre blanc';
    if (spinner) spinner.style.display = 'none';

    const resolvedPdf = this.pdfUrl ? this.normalizeUrl(this.pdfUrl) : '';

    this.showSuccess();

    if (resolvedPdf) {
      this.ensurePdfDownloadLink(resolvedPdf);
      try {
        await this.downloadPDF(this.pdfUrl);
      } catch (err) {
        console.warn('[whitepaper] downloadPDF', err);
      }
    }

    const modal = this.closest('whitepaper-download');
    if (modal) {
      const closeDelay = resolvedPdf ? 22000 : 4000;
      setTimeout(() => {
        modal.hideModal();
      }, closeDelay);
    }
  }

  async downloadPDF(url) {
    const resolved = this.normalizeUrl(url);
    if (!resolved) return;

    const filename =
      decodeURIComponent(resolved.split('/').pop().split('?')[0] || '') || 'livre-blanc.pdf';

    try {
      const res = await fetch(resolved, { mode: 'cors', credentials: 'omit' });
      if (res.ok) {
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
        return;
      }
    } catch {
      /* CDN sans CORS : repli */
    }

    const link = document.createElement('a');
    link.href = resolved;
    link.download = filename;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  showSuccess() {
    const successMessage = this.querySelector('.whitepaper-form__success');
    if (successMessage) {
      successMessage.style.display = 'block';
      successMessage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  showError(message) {
    let errorDiv = this.querySelector('.whitepaper-form__error');
    if (!errorDiv) {
      errorDiv = document.createElement('div');
      errorDiv.className = 'whitepaper-form__error alert alert--error blocks-radius';
      this.insertBefore(errorDiv, this.querySelector('.whitepaper-form__fields'));
    }
    errorDiv.innerHTML = `
      <h2 class="form-message form-message--error h6 m-0">
        ${message}
      </h2>
    `;
    errorDiv.style.display = 'block';
    errorDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

if (!customElements.get('whitepaper-form')) {
  customElements.define('whitepaper-form', WhitepaperForm, { extends: 'form' });
}

/**
 * Composant Modal pour le livre blanc
 * Utilise DrawerComponent du thème pour la compatibilité
 */
class WhitepaperDownload extends (window.DrawerComponent || HTMLElement) {
  constructor() {
    super();
    this.showHandler = null;
    this.init();
  }

  init() {
    // Attendre que le DOM soit prêt
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupTriggers();
        this.setupCloseHandlers();
      });
    } else {
      // Le DOM est déjà chargé
      setTimeout(() => {
        this.setupTriggers();
        this.setupCloseHandlers();
      }, 100);
    }
  }

  setupTriggers() {
    // Boutons qui ouvrent ce modal
    const triggers = document.querySelectorAll(`[aria-controls="${this.id}"]`);
    triggers.forEach(trigger => {
      // Retirer les anciens listeners pour éviter les doublons
      if (this.showHandler) {
        trigger.removeEventListener('click', this.showHandler);
      }
      // Créer un nouveau handler
      this.showHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.showModal();
      };
      trigger.addEventListener('click', this.showHandler);
    });
  }

  setupCloseHandlers() {
    // Bouton de fermeture
    const closeBtn = this.querySelector('.drawer__close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.hideModal());
    }

    // Overlay pour fermer
    const overlay = this.querySelector('.fixed-overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.hideModal());
    }

    // Échap pour fermer
    const escapeHandler = (e) => {
      if (e.key === 'Escape' && this.hasAttribute('open')) {
        this.hideModal();
      }
    };
    document.addEventListener('keydown', escapeHandler);
    this.escapeHandler = escapeHandler;
  }

  showModal() {
    // Utiliser la méthode show() de DrawerComponent si disponible
    if (typeof super.show === 'function') {
      this.show();
    } else {
      // Fallback si DrawerComponent n'est pas disponible
      this.setAttribute('open', '');
      this.setAttribute('active', '');
      this.removeAttribute('hidden');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    
    // Focus sur le premier champ du formulaire
    setTimeout(() => {
      const firstInput = this.querySelector('input[type="text"], input[type="email"]');
      if (firstInput) {
        firstInput.focus();
      }
    }, 200);
  }

  hideModal() {
    // Utiliser la méthode hide() de DrawerComponent si disponible
    if (typeof super.hide === 'function') {
      this.hide();
    } else {
      // Fallback si DrawerComponent n'est pas disponible
      this.removeAttribute('open');
      this.removeAttribute('active');
      this.setAttribute('hidden', '');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  }
}

if (!customElements.get('whitepaper-download')) {
  customElements.define('whitepaper-download', WhitepaperDownload);
}
})();
